import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import algoliasearch from 'algoliasearch/lite';
import {
  InstantSearch,
  SearchBox,
  Hits,
  Highlight,
  connectStateResults,
  Configure,
} from 'react-instantsearch-dom';
import SearchIcon from '@mui/icons-material/Search';

// Initialize Algolia Client
const searchClient = algoliasearch(
  'ARBNPWCP6Q',
  '0a4876dcca3c4357ad006a5914bed63d'
);

// Component for displaying search results (hits)
const Hit = ({ hit }) => {
  const handleClick = () => {
    const postUrl = `${window.location.origin}/${hit.path}`;
    window.open(postUrl, '_blank'); // Open the post page in a new window
  };
  return (
    <Box
      sx={{
        padding: { xs: 1, sm: 1, md: 2, lg: 1.5, xl: 1.5 },
        boxShadow: '0px 1px 2px grey',
        borderRadius: '8px',
        marginBottom: 2,
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        width: { xs: 'auto', sm: 'auto', md: 'auto', lg: 'auto', xl: 'auto' },
      }}
      onClick={handleClick}
    >
      <img
        src={hit.images?.[0] || '/default-image.jpg'}
        alt={hit.title}
        style={{
          width: '80px',
          height: '80px',
          marginRight: '12px',
          borderRadius: '12px',
          boxShadow: '0px 1px 2px grey',
          objectFit: 'cover',
        }}
      />
      <Box>
        <Typography
          variant='h6'
          component='h2'
          sx={{
            fontFamily: 'Montserrat',
            fontSize: {
              xs: '15px',
              sm: '15px',
              md: '16px',
              lg: '18px',
              xl: '18px',
            },
            fontWeight: '500',
            color: 'black',
            '& .ais-Highlight-highlighted': {
              fontStyle: 'italic',
              fontWeight: 'bold',
              color: '#0073e6',
            },
          }}
        >
          <Highlight attribute='title' hit={hit} highlightedTagName='span' />
        </Typography>
        <Typography
          variant='body2'
          sx={{
            fontFamily: 'Montserrat',
            fontSize: '11px',
            color: 'black',
            '& .ais-Highlight-highlighted': {
              fontStyle: 'italic',
              fontWeight: 'bold',
              color: '#0073e6',
            },
          }}
        >
          <Highlight
            attribute='description'
            hit={hit}
            highlightedTagName='span'
          />
        </Typography>
        <Typography
          variant='caption'
          display='block'
          color='darkblue'
          sx={{
            fontFamily: 'Montserrat',
            fontSize: '10px',
            '& .ais-Highlight-highlighted': {
              fontStyle: 'italic',
              fontWeight: 'bold',
              color: '#0073e6',
            },
          }}
        >
          <Highlight attribute='category' hit={hit} highlightedTagName='span' />
        </Typography>
        {/* Add username to the displayed results */}
        {hit.username ? (
          <Typography
            variant='caption'
            display='block'
            color='gray'
            sx={{
              fontFamily: 'Montserrat',
              fontSize: '10px',
              '& .ais-Highlight-highlighted': {
                fontStyle: 'italic',
                fontWeight: 'bold',
                color: '#0073e6',
              },
            }}
          >
            Posted by: {hit.username}
            {/* Posted by: {hit.username || 'Unknown'} */}
          </Typography>
        ) : (
          ''
        )}
      </Box>
    </Box>
  );
};

// Component to display hits or a message based on search state
const Results = connectStateResults(({ searchState, searchResults }) => {
  const hasResults = searchResults && searchResults.nbHits !== 0;

  if (!searchState?.query) {
    return (
      <Typography
        variant='body2'
        sx={{
          textAlign: 'center',
          mt: 0,
          fontFamily: 'Montserrat',
          color: 'black',
          fontSize: '14px',
        }}
      >
        Start typing to see results...
      </Typography>
    );
  }

  if (!hasResults) {
    return (
      <Typography
        variant='body2'
        sx={{
          textAlign: 'center',
          mt: 0,
          color: 'black',
          fontFamily: 'Montserrat',
          fontSize: '14px',
        }}
      >
        No results found
      </Typography>
    );
  }

  return <Hits hitComponent={Hit} />;
});

const SearchBar = () => {
  const [query, setQuery] = useState(''); // State to track input value
  const searchBoxRef = useRef(null);

  const handleSearchStateChange = ({ query }) => {
    setQuery(query);
  };

  const handleClose = () => {
    setOpen(false);
    setQuery(''); // Clear query when closing
   // console.log('handleClose initiated');
  };

  // Function to programmatically trigger the reset button click
  const triggerReset = () => {
    const resetButton = document.querySelector('.ais-SearchBox-reset');
    if (resetButton) {
      resetButton.click();
    }
  };

  // Handle clicks outside search area
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Get the search container and search input
      const searchContainer = searchBoxRef.current;
      // const resultsBox = document.querySelector('.ais-Hits');
      const resultsBox = document.querySelector('.search-results-box'); // Changed from .ais-Hits - affect when results are found or not found
      const searchInput = document.querySelector('.ais-SearchBox-input');

      // Ignore clicks on the search input and its children
      if (searchInput && searchInput.contains(event.target)) {
        return;
      }

      // Check if click is outside both search box and results
      if (searchContainer && resultsBox) {
        const isClickInSearch = searchContainer.contains(event.target);
        const isClickInResults = resultsBox.contains(event.target);

        if (!isClickInSearch && !isClickInResults) {
          triggerReset();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <Box sx={{ position: 'relative', marginTop: 2, padding: 2, mb: 1 }}>
      {/* <Box sx={{ marginTop: 2, padding: 2 }}> */}
      {/* <InstantSearch searchClient={searchClient} indexName="xpat360"> */}
      <InstantSearch
        searchClient={searchClient}
        indexName='xpat360'
        onSearchStateChange={handleSearchStateChange}
      >
        <Configure typoTolerance={false} />
        {/* Search Box */}
        <Box
          ref={searchBoxRef}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            margin: '0 auto',
          }}
        >
          {/* Container for SearchBox and Icon */}
          <Box
            sx={{
              position: 'relative',
              width: '100%', // Ensures the container matches the search box's width
            }}
          >
            <SearchIcon
              sx={{
                position: 'absolute',
                left: '12px', // Adjusts horizontal position
                top: '50%', // Centers vertically
                transform: 'translateY(-50%)',
                color: 'grey.500',
                fontSize: '20px',
              }}
            />
            <SearchBox
              translations={{
                placeholder: 'Search...',
              }}
              className='custom-search-box' // Apply custom styles
              submitIconComponent={() => null} // Removes default submit button
            />
          </Box>
        </Box>

        {/* Config to avoid showing all results */}
        <Configure hitsPerPage={16} query={query} />

        {/* Results */}
        {query && (
          <Box
            className='search-results-box'
            sx={{
              p: { xs: 2, sm: 2, md: 2.5, lg: 2.5, xl: 2.5 },
              position: 'absolute',
              top: '60px',
              left: { xs: '49%', sm: '50%', md: '50%', lg: '50%', xl: '50%' },
              transform: 'translateX(-50%)',
              width: {
                xs: '325px',
                sm: '360px',
                md: '400px',
                lg: '400px',
                xl: '400px',
              },
              maxHeight: {
                xs: '330px',
                sm: '400px',
                md: '500px',
                lg: '500px',
                xl: '500px',
              },
              backgroundColor: '#fff',
              border: '0.8px solid #ccc',
              borderRadius: '9px',
              overflowY: 'auto',
              zIndex: 1300, // Below search box
              boxShadow: '0 0px 6px inset rgba(0,0,0,0.1)',
            }}
          >
            <Results />
            {/* <Hits hitComponent={Hit} /> */}
          </Box>
        )}
      </InstantSearch>
    </Box>
  );
};

export default SearchBar;
